import React, { useEffect, useState } from "react";
import "./style.css";
import {
  Accordion,
  Button,
  Card,
  Col,
  Form,
  Row,
  Container,
  InputGroup,
  Alert,
  Modal,
  Stack,
  ListGroup,
} from "react-bootstrap";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import * as Icon from "react-bootstrap-icons";
import QuestionaryTypeService from "../../services/questionaryType.service";
import QuestionaryService from "../../services/questionary.service";
import SectionService from "../../services/questionarySection.service";
import SubsectionService from "../../services/questionarySubsection.service";
import { QuestionaryQuestionsManager } from "./QuestionaryQuestionsManager";
import { QuestionaryType } from "../../models/QuestionaryType";
import { QuestionarySection } from "../../models/QuestionarySection";
import { QuestionarySubsection } from "../../models/QuestionarySubsection";
import DeleteButton from "../../Helpers/deleteButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faList,
  faArrowRight,
  faArrowLeft,
  faBan,
  faAdd,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import { useLoading } from "../App/LoadingContext";

export const QuestionarySectionsManager = () => {
  const [showDetails, setShowDetails] = useState(false);
  const [policyTypes, setPolicyTypes] = useState<QuestionaryType[]>([]);
  const [sections, setSections] = useState<QuestionarySection[]>([]);
  const [policyTypeId, setPolicyTypeId] = useState<number>(-1);
  const [selectedSubSection, setSelectedSubsection] =
    useState<QuestionarySubsection>();
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [popupTitle, setPopupTitle] = useState<string>("");
  const [popupMessage, setPopupMessage] = useState<string>("");
  const [isErrorMessage, setIsErrorMessage] = useState<boolean>(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [questionaryToCloneId, setQuestionaryToCloneId] = useState<
    number | null
  >(null);
  const [newQuestionaryName, setNewQuestionaryName] = useState("");
  const [cloneError, setCloneError] = useState("");

  const [showMoveModal, setShowMoveModal] = useState(false);
  const [subsectionToMove, setSubsectionToMove] = useState<{
    subsection: QuestionarySubsection;
    fromSectionId: number;
  } | null>(null);
  const [targetSectionId, setTargetSectionId] = useState<number | string>("");
  const { wrapWithLoading } = useLoading();

  async function onPolicyTypeChange(policyTypeId: number) {
    if (!isNaN(policyTypeId)) {
      let sections = await QuestionaryService.getSections(policyTypeId);
      sections = sections
        .sort((a, b) => a.position - b.position)
        .map((sub) => ({
          ...sub,
          questionarySubsections: (sub.questionarySubsections || []).sort(
            (a, b) => a.position - b.position
          ),
        }));
      setSections(sections);
      setPolicyTypeId(policyTypeId);
    } else {
      setSections(() => []);
      setPolicyTypeId(-1);
    }
  }

  function handleShowDetails(selectedSubSection: QuestionarySubsection) {
    setSelectedSubsection(selectedSubSection);
    setShowDetails(true);
  }

  async function onSectionInsert() {
    const newPosition =
      sections.length > 0
        ? Math.max(...sections.map((s) => s.position)) + 1
        : 1;

    let section: QuestionarySection = {
      id: 0,
      name: "Nueva sección",
      questionaryTypeId: policyTypeId,
      position: newPosition,
    };

    var result = SectionService.insertSection(section);

    result
      .then((response) => {
        var resp = response as Response;

        if (resp.ok) {
          return resp.json();
        }
      })
      .then((result) => {
        section.id = result;
        setSections([...sections, section]);
      });
  }

  async function onSectionDelete(section: QuestionarySection) {
    try {
      const response = await SectionService.deleteSection(section);
      if (response.ok) {
        const filteredSections = sections.filter(
          (currentSection) => currentSection.id !== section.id
        );

        // Recalcula las posiciones de las secciones restantes
        const updatedSections = recalculatePositions(filteredSections);

        setSections(updatedSections);

        // Opcional: Si quieres persistir el nuevo orden en el backend
        await SectionService.updatePositions(
          updatedSections.map((s) => ({
            sectionId: s.id,
            newPosition: s.position,
          }))
        );

        setPopupMessage("Se eliminó la sección exitosamente");
        setPopupTitle("Registro eliminado");
        setIsErrorMessage(false);
        setShowPopup(true);
      } else {
        throw new Error("No se pudo eliminar el registro");
      }
    } catch (error) {
      setPopupMessage(
        "No se puede eliminar el registro porque hay información relacionada"
      );
      setPopupTitle("Error");
      setIsErrorMessage(true);
      setShowPopup(true);
    } finally {
      window.scrollTo(0, 0);
    }
  }

  async function onSubsectionDelete(
    subsection: QuestionarySubsection,
    sectionId: number
  ) {
    try {
      const response = await SubsectionService.deleteSubsection(subsection);
      if (response.ok) {
        const newSections = sections.map((section) => {
          if (section.id === sectionId) {
            const filteredSubsections =
              section.questionarySubsections?.filter(
                (sub) => sub.id !== subsection.id
              ) || [];

            // Recalcula las posiciones de las subsecciones restantes
            const updatedSubsections =
              recalculatePositions(filteredSubsections);

            // Devuelve una copia de la sección con las subsecciones actualizadas
            return {
              ...section,
              questionarySubsections: updatedSubsections,
            };
          }
          return section;
        });

        setSections(newSections);

        // Opcional: Persistir el nuevo orden de subsecciones en el backend
        const sectionToUpdate = newSections.find((s) => s.id === sectionId);
        if (sectionToUpdate?.questionarySubsections) {
          await SubsectionService.updatePositions(
            sectionToUpdate.questionarySubsections.map((ss) => ({
              subsectionId: ss.id,
              newPosition: ss.position,
            }))
          );
        }

        setPopupMessage("Se eliminó la subsección exitosamente");
        setPopupTitle("Registro eliminado");
        setIsErrorMessage(false);
        setShowPopup(true);
      } else {
        throw new Error("No se pudo eliminar el registro");
      }
    } catch (error) {
      setPopupMessage(
        "No se puede eliminar el registro porque hay información relacionada"
      );
      setPopupTitle("Error");
      setIsErrorMessage(true);
      setShowPopup(true);
    } finally {
      window.scrollTo(0, 0);
    }
  }

  function onSectionUpdate(sectionName: string, sectionId: number) {
    const myNewList = [...sections];
    const currentSection = myNewList.find((a) => a.id == sectionId);

    if (currentSection) {
      currentSection.name = sectionName;
      setSections(myNewList);
    }
  }

  function onSubsectionUpdate(
    subsectionName: string,
    subsectionId: number,
    sectionId: number
  ) {
    const myNewList = [...sections];
    const currentSection = myNewList.find((a) => a.id == sectionId);

    if (currentSection) {
      const currentSubsection = currentSection.questionarySubsections?.find(
        (a) => a.id == subsectionId
      );

      if (currentSubsection) {
        currentSubsection.name = subsectionName;
        setSections(myNewList);
      }
    }
  }

  async function onSubSectionInsert(section: QuestionarySection) {
    const subsections = section.questionarySubsections || [];
    const newPosition =
      subsections.length > 0
        ? Math.max(...subsections.map((ss) => ss.position)) + 1
        : 1;

    let subsection: QuestionarySubsection = {
      id: 0,
      name: "Nueva sub sección",
      questionarySectionId: section.id,
      position: newPosition,
    };

    var result = SubsectionService.insertSubsection(subsection);

    result
      .then((response) => {
        var resp = response as Response;

        if (resp.ok) {
          return resp.json();
        }
      })
      .then((result) => {
        const myNewList = [...sections];
        const currentSection = myNewList.find((a) => a.id == section.id);

        subsection.id = result;

        if (currentSection) {
          if (currentSection.questionarySubsections) {
            currentSection.questionarySubsections.push(subsection);
          } else {
            currentSection.questionarySubsections = [subsection];
          }

          setSections(myNewList);
        }
      });
  }

  async function onSectionChange(currentSection: QuestionarySection) {
    var result = SectionService.updateSection(currentSection);
  }

  async function onSubsectionChange(currentSubsection: QuestionarySubsection) {
    var result = SubsectionService.updateSubsection(currentSubsection);
  }

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    // Drag de secciones
    if (result.type === "SECTION") {
      const items = Array.from(sections);
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);

      // Actualizar posiciones locales
      const updatedSections = items.map((section, index) => ({
        ...section,
        position: index + 1,
      }));

      setSections(updatedSections);

      // Actualizar en backend
      try {
        await SectionService.updatePositions(
          updatedSections.map((s) => ({
            sectionId: s.id,
            newPosition: s.position,
          }))
        );
      } catch (error) {
        setPopupMessage("Error al actualizar el orden de las secciones");
        setPopupTitle("Error");
        setIsErrorMessage(true);
        setShowPopup(true);
      }
    }

    // Drag de subsecciones
    if (result.type === "SUBSECTION") {
      const sectionId = parseInt(result.destination.droppableId);
      const sectionIndex = sections.findIndex((s) => s.id === sectionId);
      if (sectionIndex === -1) return;

      const currentSection = { ...sections[sectionIndex] };
      const subsections = [...(currentSection.questionarySubsections || [])];

      // Reordenar
      const [reorderedItem] = subsections.splice(result.source.index, 1);
      subsections.splice(result.destination.index, 0, reorderedItem);

      // Actualizar posiciones
      const updatedSubsections = subsections.map((ss, index) => ({
        ...ss,
        position: index + 1,
      }));

      // Actualizar estado
      const newSections = [...sections];
      newSections[sectionIndex] = {
        ...currentSection,
        questionarySubsections: updatedSubsections,
      };

      setSections(newSections);

      // Actualizar backend
      try {
        await SubsectionService.updatePositions(
          updatedSubsections.map((ss) => ({
            subsectionId: ss.id,
            newPosition: ss.position,
          }))
        );
      } catch (error) {
        setPopupMessage("Error al actualizar el orden de las subsecciones");
        setPopupTitle("Error");
        setIsErrorMessage(true);
        setShowPopup(true);
      }
    }
  };

  const handleCloneClick = () => {
    if (policyTypeId <= 0) return;
    setQuestionaryToCloneId(policyTypeId);
    setNewQuestionaryName("");
    setCloneError("");
    setShowCloneModal(true);
  };

  const handleCloseCloneModal = () => {
    setShowCloneModal(false);
    setQuestionaryToCloneId(null);
    setNewQuestionaryName("");
    setCloneError("");
  };

  const handleCloneSave = async () => {
    if (!newQuestionaryName.trim()) {
      setCloneError("El nombre del nuevo cuestionario no puede estar vacío.");
      return;
    }

    if (!questionaryToCloneId) {
      setCloneError("No se ha seleccionado un cuestionario para clonar.");
      return;
    }

    try {
      const request = {
        questionaryTypeIdToClone: questionaryToCloneId,
        newQuestionaryName: newQuestionaryName,
      };

      await QuestionaryTypeService.clone(request);

      // Actualizar lista de tipos y limpiar
      const updatedPolicyTypes =
        await QuestionaryTypeService.getQuestionaryTypes();
      setPolicyTypes(updatedPolicyTypes);

      handleCloseCloneModal();
      setPopupMessage("Cuestionario clonado exitosamente");
      setPopupTitle("Clonación exitosa");
      setIsErrorMessage(false);
      setShowPopup(true);
    } catch (error: any) {
      setCloneError(`Error al clonar: ${error.message}`);
    }
  };

  useEffect(() => {
    async function loadPolicyTypes() {
      try {
        const data = await QuestionaryTypeService.getQuestionaryTypes();

        // Orden Alfabetico
        const sortedData = data.sort((a, b) => a.name.localeCompare(b.name));
        setPolicyTypes(sortedData.filter((pt) => !pt.isArchived));
      } catch (error) {
        console.error("Error loading policy types:", error);
        setPopupMessage("Error cargando tipos de cuestionario");
        setPopupTitle("Error");
        setIsErrorMessage(true);
        setShowPopup(true);
      }
    }

    loadPolicyTypes();
  }, []);

  const handleStartMove = (
    subsection: QuestionarySubsection,
    fromSectionId: number
  ) => {
    setSubsectionToMove({ subsection, fromSectionId });
    setTargetSectionId("");
    setShowMoveModal(true);
  };

  const handleCloseMoveModal = () => {
    setShowMoveModal(false);
    setSubsectionToMove(null);
    setTargetSectionId("");
  };

  const handleConfirmMove = wrapWithLoading(async (newSectionId: number) => {
    if (!subsectionToMove) return;

    const { subsection, fromSectionId } = subsectionToMove;

    // Llama al servicio del backend para mover la subsección
    await SubsectionService.moveSubsection(subsection.id, newSectionId);
    await onPolicyTypeChange(policyTypeId);

    handleCloseMoveModal();
    setPopupMessage("Subsección movida exitosamente.");
    setPopupTitle("Éxito");
    setIsErrorMessage(false);
    setShowPopup(true);
  });

  // Don't remove this section because avoid a commun issue on drag and drop functionality
  // Details on: https://github.com/atlassian/react-beautiful-dnd/issues/2396
  const [droppableId, setDroppableId] = useState("hello");
  useEffect(() => {
    if (sections.length) {
      setDroppableId("sections");
    }
  }, [sections.length]);
  //////////////////////////////////////////////////////////////////////////////////////

  const recalculatePositions = <T extends { position: number }>(
    items: T[]
  ): T[] => {
    return items
      .sort((a, b) => a.position - b.position)
      .map((item, index) => ({
        ...item,
        position: index + 1,
      }));
  };

  useEffect(() => {
    if (showPopup) {
      const timer = setTimeout(() => {
        setShowPopup(false);
      }, 6000);

      return () => clearTimeout(timer);
    }
  }, [showPopup]);

  return (
    <div>
      <div className="notification-container">
        {showPopup && (
          <Alert
            variant={isErrorMessage ? "danger" : "success"}
            onClose={() => setShowPopup(false)}
            className="notification-alert"
            dismissible
          >
            <Alert.Heading>{popupTitle}</Alert.Heading>
            <p>{popupMessage}</p>
          </Alert>
        )}
      </div>

      <Container className="pt-3">
        <DragDropContext onDragEnd={handleDragEnd}>
          <Card>
            <Card.Body>
              <Card.Title>Administrador de Cuestionarios</Card.Title>
              <hr />
              <Container>
                <Row>
                  <Col xs md={4}>
                    <Form.Select
                      aria-label=""
                      onChange={(e) =>
                        onPolicyTypeChange(parseInt(e.target.value))
                      }
                    >
                      <option>Selecciona un tipo de cuestionario</option>
                      {policyTypes.map((policyType) => (
                        <option value={policyType.id}>{policyType.name}</option>
                      ))}
                    </Form.Select>
                  </Col>
                </Row>
                <Row className="pt-3">
                  <Col>
                    {sections.length > 0 && (
                      <Droppable droppableId={droppableId} type="SECTION">
                        {(provided) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                          >
                            <Accordion>
                              {sections.map((section, sectionIndex) => (
                                <Draggable
                                  key={section.id.toString()}
                                  draggableId={`section-${section.id}`}
                                  index={sectionIndex}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      style={{
                                        ...provided.draggableProps.style,
                                        transition: "all 0.3s ease",
                                        backgroundColor: snapshot.isDragging
                                          ? "#f8f9fa"
                                          : "white",
                                        marginBottom: "10px",
                                      }}
                                    >
                                      <Accordion.Item
                                        eventKey={section.id.toString()}
                                      >
                                        <Accordion.Header>
                                          <div className="d-flex align-items-center w-100">
                                            <div
                                              {...provided.dragHandleProps}
                                              className="drag-handle me-2"
                                            >
                                              <Icon.GripVertical className="text-muted" />
                                            </div>
                                            <span className="badge bg-primary me-2">
                                              {section.position}
                                            </span>
                                            <InputGroup
                                              className="mb-0"
                                              size="sm"
                                            >
                                              <Form.Control
                                                type="text"
                                                value={section.name}
                                                onChange={(e) => {
                                                  onSectionUpdate(
                                                    e.target.value,
                                                    section.id
                                                  );
                                                }}
                                                onBlur={(e) => {
                                                  onSectionChange(section);
                                                }}
                                              />
                                              <DeleteButton
                                                onClick={() =>
                                                  onSectionDelete(section)
                                                }
                                              />
                                            </InputGroup>
                                          </div>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                          <Row>
                                            <Col
                                              xs
                                              md={{
                                                span: 10,
                                                offset: 1,
                                              }}
                                            >
                                              <Droppable
                                                droppableId={section.id.toString()}
                                                type="SUBSECTION"
                                              >
                                                {(provided) => (
                                                  <div
                                                    {...provided.droppableProps}
                                                    ref={provided.innerRef}
                                                  >
                                                    {section.questionarySubsections?.map(
                                                      (
                                                        subsection,
                                                        subsectionIndex
                                                      ) => (
                                                        <Draggable
                                                          key={subsection.id.toString()}
                                                          draggableId={`subsection-${subsection.id}`}
                                                          index={
                                                            subsectionIndex
                                                          }
                                                        >
                                                          {(
                                                            provided,
                                                            snapshot
                                                          ) => (
                                                            <div
                                                              ref={
                                                                provided.innerRef
                                                              }
                                                              {...provided.draggableProps}
                                                              style={{
                                                                ...provided
                                                                  .draggableProps
                                                                  .style,
                                                                transition:
                                                                  "all 0.3s ease",
                                                                backgroundColor:
                                                                  snapshot.isDragging
                                                                    ? "#f8f9fa"
                                                                    : "white",
                                                                marginBottom:
                                                                  "10px",
                                                              }}
                                                            >
                                                              <InputGroup
                                                                className="mb-3"
                                                                size="sm"
                                                              >
                                                                <InputGroup.Text
                                                                  {...provided.dragHandleProps}
                                                                  style={{
                                                                    cursor:
                                                                      "grab",
                                                                  }}
                                                                >
                                                                  <Icon.GripVertical />
                                                                </InputGroup.Text>
                                                                <InputGroup.Text>
                                                                  <span className="badge bg-secondary">
                                                                    {
                                                                      subsection.position
                                                                    }
                                                                  </span>
                                                                </InputGroup.Text>
                                                                <Form.Control
                                                                  type="text"
                                                                  value={
                                                                    subsection.name
                                                                  }
                                                                  onChange={(
                                                                    e
                                                                  ) => {
                                                                    onSubsectionUpdate(
                                                                      e.target
                                                                        .value,
                                                                      subsection.id,
                                                                      section.id
                                                                    );
                                                                  }}
                                                                  onBlur={(
                                                                    e
                                                                  ) => {
                                                                    onSubsectionChange(
                                                                      subsection
                                                                    );
                                                                  }}
                                                                />
                                                                <Stack
                                                                  direction="horizontal"
                                                                  gap={1}
                                                                >
                                                                  <Button
                                                                    variant="primary"
                                                                    size="sm"
                                                                    onClick={() =>
                                                                      handleShowDetails(
                                                                        subsection
                                                                      )
                                                                    }
                                                                  >
                                                                    <a>
                                                                      <FontAwesomeIcon
                                                                        icon={
                                                                          faList
                                                                        }
                                                                        className="text-white"
                                                                        style={{
                                                                          paddingRight:
                                                                            "3px",
                                                                        }}
                                                                      />
                                                                    </a>
                                                                    Detalles
                                                                  </Button>
                                                                  <Button
                                                                    variant="primary"
                                                                    size="sm"
                                                                    onClick={() =>
                                                                      handleStartMove(
                                                                        subsection,
                                                                        section.id
                                                                      )
                                                                    }
                                                                  >
                                                                    <a>
                                                                      <FontAwesomeIcon
                                                                        icon={
                                                                          faArrowRight
                                                                        }
                                                                        className="text-white"
                                                                        style={{
                                                                          paddingRight:
                                                                            "3px",
                                                                        }}
                                                                      />
                                                                      Mover
                                                                    </a>
                                                                  </Button>
                                                                  <DeleteButton
                                                                    onClick={() =>
                                                                      onSubsectionDelete(
                                                                        subsection,
                                                                        section.id
                                                                      )
                                                                    }
                                                                  />
                                                                </Stack>
                                                              </InputGroup>
                                                            </div>
                                                          )}
                                                        </Draggable>
                                                      )
                                                    )}
                                                    {provided.placeholder}
                                                  </div>
                                                )}
                                              </Droppable>
                                              <hr />
                                              <Row>
                                                <Col>
                                                  <Button
                                                    variant="primary"
                                                    onClick={() =>
                                                      onSubSectionInsert(
                                                        section
                                                      )
                                                    }
                                                    size="sm"
                                                  >
                                                    <a>
                                                      <FontAwesomeIcon
                                                        icon={faAdd}
                                                        style={{
                                                          paddingRight: "3px",
                                                        }}
                                                      />
                                                      Nueva subsección
                                                    </a>
                                                  </Button>
                                                </Col>
                                              </Row>
                                            </Col>
                                          </Row>
                                        </Accordion.Body>
                                      </Accordion.Item>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                            </Accordion>
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    )}
                  </Col>
                </Row>
                <Row className="pt-3">
                  <Col>
                    {policyTypeId > -1 && (
                      <div className="d-flex gap-2">
                        <Button variant="primary" onClick={onSectionInsert}>
                          <a>
                            <FontAwesomeIcon
                              icon={faAdd}
                              style={{
                                paddingRight: "3px",
                              }}
                            />
                            Nueva sección
                          </a>
                        </Button>
                        <Button variant="primary" onClick={handleCloneClick}>
                          <a>
                            <FontAwesomeIcon
                              icon={faCopy}
                              style={{
                                paddingRight: "3px",
                              }}
                            />
                            Clonar cuestionario
                          </a>
                        </Button>
                      </div>
                    )}
                  </Col>
                </Row>
              </Container>
            </Card.Body>
          </Card>
        </DragDropContext>
      </Container>

      {selectedSubSection && (
        <Modal
          show={showDetails}
          fullscreen={true}
          onHide={() => setShowDetails(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>Preguntas</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <QuestionaryQuestionsManager
              subsection={selectedSubSection}
            ></QuestionaryQuestionsManager>
          </Modal.Body>
        </Modal>
      )}

      <Modal show={showCloneModal} onHide={handleCloseCloneModal}>
        <Modal.Header closeButton>
          <Modal.Title>Clonar cuestionario</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {cloneError && <Alert variant="danger">{cloneError}</Alert>}
          <Form.Group>
            <Form.Label>Nuevo nombre del cuestionario:</Form.Label>
            <Form.Control
              type="text"
              placeholder="Ingrese el nuevo nombre..."
              value={newQuestionaryName}
              onChange={(e) => setNewQuestionaryName(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCloneModal}>
            <a>
              <FontAwesomeIcon
                icon={faBan}
                style={{
                  paddingRight: "3px",
                }}
              />
              Cancelar
            </a>
          </Button>
          <Button variant="primary" onClick={handleCloneSave}>
            <a>
              <FontAwesomeIcon
                icon={faCopy}
                className="text-white"
                style={{
                  paddingRight: "3px",
                }}
              />
              Clonar
            </a>
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showMoveModal}
        onHide={handleCloseMoveModal}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Mover Subsección</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Mover la subsección:{" "}
            <strong>{subsectionToMove?.subsection.name}</strong>
          </p>
          <p>Selecciona la nueva sección de destino:</p>
          <ListGroup style={{ maxHeight: "400px", overflowY: "auto" }}>
            {sections
              .filter((s) => s.id !== subsectionToMove?.fromSectionId)
              .map((section) => (
                <ListGroup.Item
                  key={section.id}
                  action
                  className="d-flex justify-content-between align-items-center"
                >
                  <span>
                    <span className="badge bg-primary me-2">
                      {section.position}
                    </span>
                    {section.name}
                  </span>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleConfirmMove(section.id)}
                  >
                    <a>
                      <FontAwesomeIcon
                        icon={faArrowLeft}
                        style={{
                          paddingRight: "3px",
                        }}
                      />
                      Mover aquí
                    </a>
                  </Button>
                </ListGroup.Item>
              ))}
          </ListGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseMoveModal}>
            <a>
              <FontAwesomeIcon
                icon={faBan}
                style={{
                  paddingRight: "3px",
                }}
              />
              Cancelar
            </a>
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
